import axios, { AxiosProgressEvent, AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import log from 'loglevel';

/**
 * Allows handling of progress events for downloads.
 */
export type AxiosProgressHandler = (event: AxiosProgressEvent) => void;

/**
 * Default handling of download progress events.
 * @param event the axios progress event.
 */
export const debugProgressHandler: AxiosProgressHandler = (event: AxiosProgressEvent) => {
  if (event?.loaded && event?.total) {
    const percentage = Math.round((event.loaded * 100) / event.total);
    log.debug(`Cache Download Progress Event: ${percentage}%`);
  }
};

/**
 * Fetch remote.
 * @param request the request info.
 * @param onDownloadProgress the download progress handler.
 * @returns
 */
const axiosFetch = async (
  request: RequestInfo,
  onDownloadProgress?: AxiosProgressHandler,
): Promise<Response> => {
  const req: Request = new Request(request);

  const config: AxiosRequestConfig = {
    url: req.url,
    method: 'get',
    withCredentials: true,
    headers: req.headers as unknown as AxiosRequestHeaders,
    responseType: 'blob',
    onDownloadProgress,
  };

  const client = axios.create(config);
  const response = await client.get(req.url, config);

  if (response.status !== 200) {
    return Promise.reject(response.statusText);
  }

  return Promise.resolve(
    new Response(response.data, {
      headers: {
        'content-length': response.headers['content-length'],
        'content-type': response.headers['content-type'],
      },
      status: response.status,
      statusText: response.statusText,
    }),
  );
};

/**
 * Fetch resource with cache.
 * @param cache the cache.
 * @param request the request info.
 * @param onDownloadProgress the download progress handler.
 */
export const fetchCache = (
  cache: Cache,
  request: RequestInfo,
  onDownloadProgress?: AxiosProgressHandler,
): Promise<Response> => {
  return cache.match(request).then(item => {
    // Match found return.
    if (item) {
      return item;
    }
    // Match not found then fetch, save and return.
    return axiosFetch(request, onDownloadProgress).then(response =>
      cache.put(request, response.clone()).then(() => response),
    );
  });
};

/**
 * Fetch multiple resources with cache.
 * @param cache the cache.
 * @param request the requests info.
 * @param onDownloadProgress the download progress handler.
 */
export const cacheFetchAll = (
  cache: Cache,
  requests: RequestInfo[],
  onDownloadProgress?: AxiosProgressHandler,
): Promise<Response[]> => {
  return Promise.all(requests.map(request => fetchCache(cache, request, onDownloadProgress)));
};
